/**
 * Patient-facing slot-picker for paid Video Consultations.
 * Public page (case UUID = unguessable). Loads /api/consultations/{cid}/video-slots
 * and lets the patient tap one to book it. Success screen shows the Meet link.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, Video, CheckCircle2, XCircle, Calendar, Clock } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ScheduleVideo() {
  const { cid } = useParams();
  const [phase, setPhase] = useState("loading");
  const [msg, setMsg] = useState("Loading available slots…");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/api/consultations/${cid}/video-slots`);
      setData(r.data);
      setPhase(r.data.current_slot_at ? "booked" : "picking");
    } catch (e) {
      setPhase("error");
      setMsg(e.response?.data?.detail || "Could not load slots.");
    }
  };

  useEffect(() => { load(); }, [cid]);   // eslint-disable-line react-hooks/exhaustive-deps

  const book = async (slot) => {
    setBusy(slot.slot_at);
    try {
      const r = await axios.post(`${API_BASE}/api/consultations/${cid}/video-slots/book`, {
        slot_at: slot.slot_at,
      });
      setData((d) => ({ ...d, current_slot_at: r.data.slot_at, video_meet_url: r.data.video_meet_url }));
      setPhase("booked");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Booking failed — pick another slot.");
      await load();  // re-fetch to show up-to-date availability
    } finally { setBusy(null); }
  };

  const currentSlotDisplay = () => {
    if (!data?.current_slot_at) return null;
    const found = (data.slots || []).find((s) => s.slot_at === data.current_slot_at);
    if (found) return found.display_ist;
    try { return new Date(data.current_slot_at).toLocaleString(); } catch (e) { return data.current_slot_at; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-violet-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6" data-testid="schedule-video-panel">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center">
            {phase === "loading" && <Loader2 className="w-7 h-7 text-violet-600 animate-spin"/>}
            {phase === "picking" && <Video className="w-7 h-7 text-violet-600"/>}
            {phase === "booked" && <CheckCircle2 className="w-8 h-8 text-emerald-600"/>}
            {phase === "error" && <XCircle className="w-8 h-8 text-red-500"/>}
          </div>
        </div>
        <h1 className="font-heading text-xl font-semibold text-slate-900 text-center mb-1">
          {phase === "booked" ? "Slot confirmed" : phase === "error" ? "Something went wrong" : "Pick your video slot"}
        </h1>
        <p className="text-sm text-slate-600 mb-4 text-center" data-testid="schedule-video-msg">
          {phase === "loading" && msg}
          {phase === "picking" && `Case ${data?.case_ref} · Dr. ${data?.doctor_name || "your specialist"} · 20-minute HD call`}
          {phase === "booked" && `You're on ${currentSlotDisplay()}. Save the link below — you'll get a WhatsApp reminder 15 minutes before.`}
          {phase === "error" && msg}
        </p>

        {phase === "booked" && data?.video_meet_url && (
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 mb-4" data-testid="booked-meet-panel">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Your Jitsi Meet room</div>
            <a
              href={data.video_meet_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block text-sm font-mono text-emerald-900 break-all underline"
              data-testid="booked-meet-link"
            >{data.video_meet_url}</a>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a href={data.video_meet_url} target="_blank" rel="noopener noreferrer">
                <Button className="h-9 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="join-now-btn">
                  <Video className="w-3.5 h-3.5 mr-1"/> Join now
                </Button>
              </a>
              <button
                type="button"
                className="h-9 px-3 text-xs rounded-md border border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-100"
                onClick={() => setPhase("picking")}
                data-testid="change-slot-btn"
              >
                Change slot
              </button>
            </div>
          </div>
        )}

        {phase === "picking" && (
          <div className="space-y-3" data-testid="slots-list">
            {(data?.slots || []).length === 0 && (
              <p className="text-sm text-slate-500 text-center">No open slots this week — WhatsApp us and we&apos;ll fit you in.</p>
            )}
            {(() => {
              // Group slots by day for a compact layout.
              const byDay = {};
              (data?.slots || []).forEach((s) => {
                const [day] = s.display_ist.split("·");
                (byDay[day.trim()] ||= []).push(s);
              });
              return Object.entries(byDay).map(([day, list]) => (
                <div key={day}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1.5">
                    <Calendar className="w-3.5 h-3.5"/> {day}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {list.map((s) => {
                      const time = s.display_ist.split("·")[1]?.trim() || s.display_ist;
                      const isBusy = busy === s.slot_at;
                      return (
                        <button
                          key={s.slot_at}
                          type="button"
                          disabled={s.is_booked || isBusy}
                          onClick={() => book(s)}
                          data-testid={`slot-${s.slot_at}`}
                          className={
                            "h-10 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 transition " +
                            (s.is_booked
                              ? "bg-slate-100 border-slate-200 text-slate-400 line-through cursor-not-allowed"
                              : isBusy
                                ? "bg-violet-100 border-violet-300 text-violet-700"
                                : "bg-white border-slate-200 text-slate-800 hover:bg-violet-50 hover:border-violet-300")
                          }
                        >
                          <Clock className="w-3 h-3"/> {time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-slate-100 text-[10px] text-slate-400 text-center">
          Powered by Jitsi Meet · Free · Encrypted · Works on any browser
        </div>
      </Card>
    </div>
  );
}
